import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, Sparkle, CircleNotch, Check, Target, Pencil, FloppyDisk, Copy } from "@phosphor-icons/react";
import { API, getAuthHeaders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Modal for writing a full script for a specific series episode.
 * Pre-fills topic / takeaway / CTA from the episode (which already carries
 * soft-CTA or final-CTA via reverse-engineered prompt).
 * User can edit everything, regenerate, tweak the script text inline, and save back.
 */
const EpisodeScriptModal = ({ open, episode, series, onClose }) => {
  const { refreshUser } = useAuth();
  const isFinalEpisode = episode && series && episode.episode_number === (series.episodes?.length || series.episode_count);

  const [platform, setPlatform] = useState("tiktok");
  const [length, setLength] = useState("45-60sec");
  const [tone, setTone] = useState("gossip-wisdom");
  const [angle, setAngle] = useState("");
  const [topic, setTopic] = useState("");
  const [goal, setGoal] = useState("");
  const [takeaway, setTakeaway] = useState("");
  const [cta, setCta] = useState("");

  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [scriptText, setScriptText] = useState("");
  const [pipelineId, setPipelineId] = useState(null);
  const [error, setError] = useState(null);

  // Reset state when modal opens with a new episode
  useEffect(() => {
    if (!open || !episode || !series) return;
    setPlatform("tiktok");
    setLength("45-60sec");
    setTone("gossip-wisdom");
    setAngle("storytelling - share a story");
    setTopic(`Episode ${episode.episode_number}: ${episode.title || ""}${episode.synopsis ? ` — ${episode.synopsis}` : ""}`);
    setGoal(series.end_goal || "");
    setTakeaway(episode.takeaway || "");
    setCta(episode.cta || (isFinalEpisode ? (series.final_cta || "") : "Follow so you don't miss the next episode."));
    setScriptText("");
    setPipelineId(null);
    setSaved(false);
    setError(null);
  }, [open, episode, series, isFinalEpisode]);

  if (!open || !episode) return null;

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/script/generate`,
        {
          platform, length, tone, angle, topic,
          goal, takeaway, cta,
          series_id: series.id,
          episode_number: episode.episode_number,
        },
        { headers: getAuthHeaders() }
      );
      setScriptText(res.data.script || "");
      setPipelineId(res.data.pipeline_id);
      await refreshUser();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to generate script");
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!pipelineId) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/pipeline/${pipelineId}`,
        { script: scriptText, cta, takeaway, goal },
        { headers: getAuthHeaders() }
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save edits");
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(scriptText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-[9998] flex items-stretch justify-end"
        onClick={onClose}
        data-testid="episode-script-modal"
      >
        <motion.div
          initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 280 }}
          className="w-full sm:w-[90%] md:w-[780px] lg:w-[880px] h-full bg-white shadow-2xl overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isFinalEpisode ? "bg-[#E91E8C] text-white" : "bg-[#E91E8C]/10 text-[#E91E8C]"}`}>
                {isFinalEpisode ? <Target size={20} weight="fill" /> : <Sparkle size={20} weight="duotone" />}
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                  {isFinalEpisode ? "Final · Conversion Episode" : `Episode ${episode.episode_number} of ${series.episodes?.length || series.episode_count}`}
                </p>
                <h2 className="text-lg font-bold text-zinc-900">{episode.title || `Episode ${episode.episode_number}`}</h2>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="close-episode-script-modal"><X size={18} /></button>
          </div>

          <div className="p-6 space-y-5">
            {/* Context banner */}
            <div className={`p-3 rounded-xl border ${isFinalEpisode ? "bg-gradient-to-br from-[#E91E8C]/10 to-pink-50 border-[#E91E8C]/30" : "bg-zinc-50 border-zinc-200"}`}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Series arc context</p>
              <p className="text-xs text-zinc-600 mt-1 leading-relaxed">
                <strong>Series:</strong> {series.title}<br/>
                <strong>End goal:</strong> {series.end_goal || "—"}<br/>
                <strong>This episode's role:</strong> {isFinalEpisode
                  ? "Deliver the conversion — close with the final CTA verbatim."
                  : "Land ONE takeaway that compounds trust. End with a soft stepping-stone CTA."}
              </p>
            </div>

            {/* Editable framework */}
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1.5">Topic</label>
                <textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={2} className="input-field text-sm resize-none" data-testid="script-modal-topic" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1.5">Key takeaway <span className="text-zinc-300 font-normal normal-case">(the ONE thing viewers remember)</span></label>
                <textarea value={takeaway} onChange={(e) => setTakeaway(e.target.value)} rows={2} className="input-field text-sm resize-none" data-testid="script-modal-takeaway" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1.5">
                  {isFinalEpisode ? <>Final CTA <span className="text-[#E91E8C]">(verbatim · conversion moment)</span></> : <>Stepping-stone CTA <span className="text-zinc-300 font-normal normal-case">(soft · keep them watching)</span></>}
                </label>
                <input value={cta} onChange={(e) => setCta(e.target.value)} className="input-field text-sm" data-testid="script-modal-cta" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 block mb-1">Platform</label>
                  <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="input-field text-xs" data-testid="script-modal-platform">
                    <option value="tiktok">TikTok</option>
                    <option value="instagram">Instagram</option>
                    <option value="youtube">YouTube Shorts</option>
                    <option value="linkedin">LinkedIn</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 block mb-1">Length</label>
                  <select value={length} onChange={(e) => setLength(e.target.value)} className="input-field text-xs" data-testid="script-modal-length">
                    <option value="15sec">15 sec</option>
                    <option value="30sec">30 sec</option>
                    <option value="45-60sec">45-60 sec</option>
                    <option value="1-2min">1-2 min</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 block mb-1">Tone</label>
                  <select value={tone} onChange={(e) => setTone(e.target.value)} className="input-field text-xs" data-testid="script-modal-tone">
                    <option value="gossip-wisdom">Gossip + Wisdom</option>
                    <option value="reflective">Reflective</option>
                    <option value="hard-truth">Hard truth</option>
                    <option value="storytelling">Storytelling</option>
                    <option value="teaching">Teaching</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 block mb-1">Angle</label>
                  <input value={angle} onChange={(e) => setAngle(e.target.value)} className="input-field text-xs" placeholder="e.g., storytelling" data-testid="script-modal-angle" />
                </div>
              </div>
            </div>

            {/* Generate / Regenerate */}
            <button
              onClick={handleGenerate}
              disabled={generating || !topic.trim() || !takeaway.trim() || !cta.trim()}
              data-testid="script-modal-generate"
              className="w-full bg-[#E91E8C] text-white rounded-xl py-3 font-semibold hover:bg-[#d4177d] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {generating ? <><CircleNotch size={16} className="animate-spin" /> Writing your script...</> : <><Sparkle size={16} weight="fill" /> {scriptText ? "Regenerate" : "Generate Script"}</>}
            </button>

            {error && <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>}

            {/* Script output — editable */}
            {scriptText && (
              <div className="space-y-2" data-testid="script-modal-output">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                    <Pencil size={12} /> Your script <span className="text-zinc-300 font-normal normal-case">(edit freely)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <button onClick={handleCopy} className="text-xs text-zinc-500 hover:text-[#E91E8C] flex items-center gap-1" data-testid="script-modal-copy">
                      {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                    </button>
                    <button onClick={handleSave} disabled={saving || !pipelineId} className="text-xs font-semibold text-white bg-[#E91E8C] hover:bg-[#d4177d] disabled:opacity-50 rounded-lg px-3 py-1.5 flex items-center gap-1" data-testid="script-modal-save">
                      {saving ? <CircleNotch size={12} className="animate-spin" /> : saved ? <><Check size={12} /> Saved</> : <><FloppyDisk size={12} /> Save edits</>}
                    </button>
                  </div>
                </div>
                <textarea
                  value={scriptText}
                  onChange={(e) => setScriptText(e.target.value)}
                  rows={18}
                  className="w-full p-4 border border-zinc-200 rounded-xl text-sm font-mono leading-relaxed resize-y focus:border-[#E91E8C] focus:ring-1 focus:ring-[#E91E8C] outline-none"
                  data-testid="script-modal-text"
                />
                <p className="text-[11px] text-zinc-400">
                  Every edit you make here is private to this episode's script — the series storyboard stays intact.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EpisodeScriptModal;
